import { gtConfirm } from '../../components/GtConfirm';
import React, { useState, useEffect, useCallback } from 'react';
import GtLoader from '../../components/GtLoader';

// Signal types in priority order. Colours follow the house palette: teal for
// the strongest buying signal, amber for money, purple for people, blue for
// footprint, slate for everything else.
const TYPE_META = {
  job_advert:        { label: 'Job Advert',         cls: 'gti-t-job',   hint: 'A live vacancy. The strongest signal there is.' },
  funding:           { label: 'Funding Round',      cls: 'gti-t-fund',  hint: 'Money in usually means hiring next.' },
  leadership_change: { label: 'Leadership Change',  cls: 'gti-t-lead',  hint: 'New leaders build new teams.' },
  expansion:         { label: 'Expansion',          cls: 'gti-t-exp',   hint: 'New space gets filled with people.' },
  hiring:            { label: 'Hiring Activity',    cls: 'gti-t-hire',  hint: 'Stated intent to grow.' },
  tech_change:       { label: 'Technology Change',  cls: 'gti-t-tech',  hint: 'New stack, new skills needed.' },
  news:              { label: 'Company News',       cls: 'gti-t-news',  hint: 'Context, not a trigger.' },
};

const TYPE_ORDER = ['job_advert', 'funding', 'leadership_change', 'expansion', 'hiring', 'tech_change', 'news'];

function ukDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function ago(v) {
  if (!v) return null;
  const days = Math.floor((Date.now() - new Date(v).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return days + ' days ago';
}

function hostOf(url) {
  if (!url) return '';
  try { return new URL(url).hostname.replace('www.', ''); } catch (_) { return url.slice(0, 40); }
}

export default function GtIntelligence({ clientId, clientName, authFetch, onSwept }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);
  const [digesting, setDigesting] = useState(false);
  const [err, setErr] = useState('');
  const [flash, setFlash] = useState('');
  const [filter, setFilter] = useState('all');
  const [editingDigest, setEditingDigest] = useState(null);
  const [digestDraft, setDigestDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await authFetch('/api/clients/' + clientId + '/intelligence');
      setData(r);
    } catch (e) {
      setErr(e.message || 'Could not load intelligence');
    }
    setLoading(false);
  }, [authFetch, clientId]);

  useEffect(() => { load(); }, [load]);

  const runSweep = async () => {
    setSweeping(true);
    setErr('');
    setFlash('');
    try {
      const r = await authFetch('/api/clients/' + clientId + '/sweep', { method: 'POST' });
      setFlash(r.message + (r.cache_hit ? ' (from cache, no cost)' : ''));
      await load();
      if (onSwept) onSwept();
    } catch (e) {
      setErr(e.message || 'Sweep failed');
    }
    setSweeping(false);
  };

  const runDigest = async () => {
    setDigesting(true);
    setErr('');
    try {
      await authFetch('/api/clients/' + clientId + '/digest', { method: 'POST' });
      await load();
    } catch (e) {
      setErr(e.message || 'Could not generate a brief');
    }
    setDigesting(false);
  };

  const deleteSignal = async (id) => {
    const confirmed = await gtConfirm({
      title: 'Remove this signal',
      message: 'It will come back on the next sweep if it is still live.',
      confirmLabel: 'Remove',
    });
    if (!confirmed) return;
    try {
      await authFetch('/api/signals/' + id, { method: 'DELETE' });
      await load();
    } catch (e) {
      setErr(e.message || 'Could not remove signal');
    }
  };

  const saveDigest = async (id) => {
    try {
      await authFetch('/api/digests/' + id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ edited_digest: digestDraft }),
      });
      setEditingDigest(null);
      await load();
    } catch (e) {
      setErr(e.message || 'Could not save');
    }
  };

  if (loading && !data) return <GtLoader compact />;

  const signals = (data && data.signals) || [];
  const digests = (data && data.digests) || [];
  const meta = (data && data.sweep_meta) || {};
  const shown = filter === 'all' ? signals : signals.filter(s => s.signal_type === filter);

  const counts = {};
  signals.forEach(s => { counts[s.signal_type] = (counts[s.signal_type] || 0) + 1; });
  const presentTypes = TYPE_ORDER.filter(t => counts[t]);

  const staleness = meta.last_swept_at
    ? 'Last swept ' + ago(meta.last_swept_at) + ' · ' + (meta.last_sweep_signal_count || 0) + ' found'
    : 'Never swept';

  const latestDigest = digests[0];

  return (
    <div className="gtr-panel gti-panel">

      <div className="gti-head">
        <div className="gti-head-l">
          <div className={'gti-stale gti-stale--' + (meta.sweep_status || 'never')}>
            <span className="gti-staledot" />
            {staleness}
          </div>
          {meta.sweep_count > 0 && (
            <div className="gti-substale">{signals.length} signal{signals.length === 1 ? '' : 's'} on file · swept {meta.sweep_count} time{meta.sweep_count === 1 ? '' : 's'}</div>
          )}
        </div>
        <button className="gtc-primary gti-sweepbtn" onClick={runSweep} disabled={sweeping}>
          {sweeping ? 'Sweeping…' : (meta.sweep_count > 0 ? 'Sweep again' : 'Run sweep')}
        </button>
      </div>

      {flash && <div className="gti-flash">{flash}</div>}
      {err && <div className="gti-err">{err}</div>}

      {signals.length === 0 && !sweeping && (
        <div className="gtr-emptytab">
          No signals yet for {clientName}. A sweep searches the open web, job boards and LinkedIn for
          evidence this company is about to need people: live vacancies, funding, office moves,
          senior appointments. Press Run sweep to look now.
        </div>
      )}

      {signals.length > 0 && (
        <>
          <div className="gti-brief">
            <div className="gti-brief-h">
              <span>Analyst brief</span>
              <button className="gtr-ccard-link" onClick={runDigest} disabled={digesting}>
                {digesting ? 'Writing…' : (latestDigest ? 'Regenerate' : 'Generate brief')}
              </button>
            </div>
            {!latestDigest ? (
              <div className="gti-brief-empty">No brief yet. Generate one to get a read on what these signals mean for hiring demand.</div>
            ) : editingDigest === latestDigest.id ? (
              <div className="gti-brief-edit">
                <textarea rows={10} value={digestDraft} onChange={e => setDigestDraft(e.target.value)} />
                <div className="gti-brief-acts">
                  <button className="gtc-primary" onClick={() => saveDigest(latestDigest.id)}>Save</button>
                  <button className="gtc-btn-ghost" onClick={() => setEditingDigest(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <div className="gti-brief-body">
                  {(latestDigest.edited_digest || latestDigest.generated_digest || '').split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
                </div>
                <div className="gti-brief-foot">
                  <span>{ukDate(latestDigest.created_at)}{latestDigest.edited_digest ? ' · edited' : ''}</span>
                  <button className="gtr-ccard-link" onClick={() => { setEditingDigest(latestDigest.id); setDigestDraft(latestDigest.edited_digest || latestDigest.generated_digest || ''); }}>Edit</button>
                </div>
              </>
            )}
          </div>

          <div className="gti-filterbar">
            <button className={'gta-chip' + (filter === 'all' ? ' gta-chip--on' : '')} onClick={() => setFilter('all')}>All {signals.length}</button>
            {presentTypes.map(t => (
              <button key={t} className={'gta-chip ' + TYPE_META[t].cls + (filter === t ? ' gta-chip--on' : '')} onClick={() => setFilter(t)}>
                <span className={'gta-chipdot ' + TYPE_META[t].cls} />{TYPE_META[t].label} {counts[t]}
              </button>
            ))}
          </div>

          <ol className="gti-list">
            {shown.map(s => {
              const m2 = TYPE_META[s.signal_type] || TYPE_META.news;
              const summary = (s.signal_data && s.signal_data.summary) || '';
              return (
                <li key={s.id} className="gti-item">
                  <span className={'gti-score ' + m2.cls} title={m2.hint}>{s.relevance_score}</span>
                  <div className="gti-item-body">
                    <div className="gti-item-top">
                      <span className={'gti-type ' + m2.cls}>{m2.label}</span>
                      <span className="gti-when">{s.detected_at ? ukDate(s.detected_at) : 'date unknown'}</span>
                    </div>
                    <div className="gti-summary">{summary}</div>
                    {s.source_url && (
                      <a className="gti-src" href={s.source_url} target="_blank" rel="noreferrer">{hostOf(s.source_url)} →</a>
                    )}
                  </div>
                  <button className="gti-del" onClick={() => deleteSignal(s.id)} title="Remove this signal">×</button>
                </li>
              );
            })}
          </ol>

          {shown.length === 0 && <div className="gtr-emptytab">No signals of this type.</div>}
        </>
      )}
    </div>
  );
}
